import { supabase } from '@/utils/supabase/client';
import { loadKitBrowserModel } from './kit-vision-runtime-service';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const asUuid = (value: string | null | undefined) => {
  const trimmed = String(value ?? '').trim();
  return UUID_RE.test(trimmed) ? trimmed : undefined;
};

// Minimal shapes for the live camera view. The production AQL schema stores
// captures/readings through device links, so this service resolves the scope
// before querying the high-frequency tables.

export interface LiveCapture {
  id: string;
  zone_id: string | null;
  image_url: string | null;
  captured_at: string;
  species: string | null;
  phase: string | null;
}

export interface LiveSensorReading {
  zone_id: string | null;
  temperature: number | null;
  ph: number | null;
  oxygen: number | null;
  salinity: number | null;
  recorded_at: string;
}

export interface LiveZoneContext {
  zoneId: string;
  zoneName: string | null;
  zoneCode: string | null;
  locationId: string | null;
}

export interface LiveCameraFeed {
  url: string;
  path: string;
  cameraId: string | null;
  kitId: string | null;
  cameraCode: string | null;
  cameraName: string | null;
  lastFrameAt: string | null;
  lastFrameStatus: string | null;
  isPrimary: boolean;
  source: 'live_feed_path' | 'camera_source_uri' | 'camera_convention';
  inference: LiveInferenceConfiguration;
  training: LiveTrainingBurstState;
}

export interface LiveInferenceConfiguration {
  mode: 'browser-onnx' | 'edge-pytorch' | 'video-only' | 'unsupported';
  modelUrl: string | null;
  modelName: string | null;
  labels: string[];
  inputSize: number;
  confidenceThreshold: number;
  source: 'kit' | 'none';
  version: number | null;
}

export interface LiveTrainingBurstState {
  available: boolean;
  active: boolean;
  target: number;
  count: number;
  folderPath: string | null;
  startedAt: string | null;
  completedAt: string | null;
}

export interface LiveTrainingDatasetImage {
  name: string;
  path: string;
  url: string;
  size: number | null;
  contentType: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface LiveTrainingDatasetExport {
  bucket: string;
  folderPath: string | null;
  imageCount: number;
  totalFolderImageCount: number;
  training: LiveTrainingBurstState | null;
  images: LiveTrainingDatasetImage[];
}

const firstString = (row: Record<string, unknown>, keys: string[]): string | null => {
  for (const key of keys) {
    const value = row[key];
    if (value === null || value === undefined) continue;
    const str = String(value).trim();
    if (str) return str;
  }
  return null;
};

const firstNumber = (row: Record<string, unknown>, keys: string[]): number | null => {
  for (const key of keys) {
    const value = row[key];
    if (value === null || value === undefined || value === '') continue;
    const parsed = typeof value === 'number' ? value : Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
};

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};

const readFunctionErrorContext = async (context: unknown) => {
  if (!context) return { status: null as number | null, message: null as string | null };

  if (context instanceof Response) {
    const payload = await context.clone().json().catch(() => null);
    const row = asRecord(payload);
    return {
      status: context.status,
      message: typeof row.error === 'string'
        ? row.error
        : typeof row.message === 'string'
          ? row.message
          : null
    };
  }

  const row = asRecord(context);
  const body = asRecord(row.body);
  return {
    status: Number.isFinite(Number(row.status)) ? Number(row.status) : null,
    message: typeof row.error === 'string'
      ? row.error
      : typeof row.message === 'string'
        ? row.message
        : typeof body.error === 'string'
          ? body.error
          : typeof body.message === 'string'
            ? body.message
            : null
  };
};

const uniqueStrings = (values: unknown[]): string[] =>
  Array.from(new Set(values.map((value) => String(value ?? '').trim()).filter(Boolean)));

const publicCaptureUrl = (path: string) =>
  supabase.storage.from('captures').getPublicUrl(path).data.publicUrl;

const normalizeCapturePath = (value: unknown): string | null => {
  const raw = String(value ?? '').trim();
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw)) return raw;
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(raw) && !/^storage:\/\//i.test(raw)) return null;
  const withoutScheme = raw.replace(/^storage:\/\//i, '').replace(/^\/+/, '');
  return withoutScheme.replace(/^captures\//i, '') || null;
};

const normalizeCameraCode = (value: unknown) =>
  String(value ?? '')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 80);

const trainingPathForCamera = (cameraCode: string | null, cameraId: string | null, configuredPath?: unknown) => {
  const configured = normalizeCapturePath(configuredPath);
  if (configured && !/\/feed\.jpg$/i.test(configured)) return configured.replace(/\/+$/g, '');
  const folder = normalizeCameraCode(cameraCode) || normalizeCameraCode(cameraId) || 'camera';
  return `live/${folder}/training`;
};

const trainingStateFromCamera = (camera: Record<string, unknown>, cameraCode: string | null, cameraId: string | null): LiveTrainingBurstState => {
  const target = firstNumber(camera, ['training_burst_target']) ?? 50;
  const count = firstNumber(camera, ['training_burst_count']) ?? 0;
  const startedAt = firstString(camera, ['training_burst_started_at']);
  const completedAt = firstString(camera, ['training_burst_completed_at']);
  return {
    available: camera.training_burst_available === true,
    active: camera.training_burst_enabled === true && Boolean(startedAt),
    target: Math.max(1, Math.min(500, Math.floor(target))),
    count: Math.max(0, Math.floor(count)),
    folderPath: firstString(camera, ['training_burst_path']) ?? trainingPathForCamera(cameraCode, cameraId),
    startedAt,
    completedAt
  };
};

const trainingStateFromPayload = (payload: unknown): LiveTrainingBurstState | null => {
  const row = asRecord(payload);
  if (!Object.keys(row).length) return null;
  const target = firstNumber(row, ['target', 'training_burst_target']) ?? 50;
  const count = firstNumber(row, ['count', 'training_burst_count']) ?? 0;
  const folderPath = firstString(row, ['folderPath', 'folder_path', 'training_burst_path']);
  const startedAt = firstString(row, ['startedAt', 'started_at', 'training_burst_started_at']);
  const completedAt = firstString(row, ['completedAt', 'completed_at', 'training_burst_completed_at']);

  return {
    available: row.available === true || row.training_burst_available === true,
    active: row.active === true,
    target: Math.max(1, Math.min(500, Math.floor(target))),
    count: Math.max(0, Math.floor(count)),
    folderPath,
    startedAt,
    completedAt
  };
};

const invokeTrainingControl = async (
  action: 'status' | 'start' | 'stop' | 'copy-current',
  params: { cameraId?: string | null; cameraCode?: string | null; target?: number | null }
): Promise<LiveTrainingBurstState | null> => {
  const body = {
    camera_id: params.cameraId ?? null,
    camera_code: params.cameraCode ?? null,
    target: params.target ?? undefined
  };
  const { data, error } = await supabase.functions.invoke(`vision-captures/training-${action}`, { body });
  if (error) {
    let detail = error.message;
    const context = (error as { context?: unknown }).context;
    if (context) {
      const parsed = await readFunctionErrorContext(context);
      detail = parsed.message ? `${parsed.status ?? ''}: ${parsed.message}`.trim() : parsed.status ? `${parsed.status}: ${detail}` : detail;
    }
    throw new Error(detail);
  }

  const payload = asRecord(data);
  if (payload.error) throw new Error(String(payload.error));
  return trainingStateFromPayload(payload.training);
};

const handleQueryError = <T,>(result: { data: unknown; error: unknown }, context: string): T => {
  if (result.error) {
    const message = result.error instanceof Error
      ? result.error.message
      : (result.error as { message?: string }).message ?? String(result.error);
    throw new Error(`${context}: ${message}`);
  }
  return (result.data ?? null) as unknown as T;
};

const fetchZoneKitIds = async (zoneId: string): Promise<string[]> => {
  const safeZoneId = asUuid(zoneId);
  if (!safeZoneId) return [];
  const [kitResult, cameraResult, sensorResult] = await Promise.all([
    supabase.from('aql_kits').select('kit_id').eq('zone_id', safeZoneId),
    supabase.from('aql_cameras').select('kit_id').eq('zone_id', safeZoneId),
    supabase.from('aql_sensors').select('kit_id').eq('zone_id', safeZoneId)
  ]);

  const kitRows = handleQueryError<Record<string, unknown>[]>({ data: kitResult.data, error: kitResult.error }, 'Failed to load zone kits') ?? [];
  const cameraRows = handleQueryError<Record<string, unknown>[]>({ data: cameraResult.data, error: cameraResult.error }, 'Failed to load zone cameras') ?? [];
  const sensorRows = handleQueryError<Record<string, unknown>[]>({ data: sensorResult.data, error: sensorResult.error }, 'Failed to load zone sensors') ?? [];

  return uniqueStrings([...kitRows, ...cameraRows, ...sensorRows].map((row) => row.kit_id));
};

const fetchScopedKitIds = async (params: {
  zoneId?: string;
  clientId?: string;
  projectId?: string;
}): Promise<string[]> => {
  const zoneId = asUuid(params.zoneId);
  let query = supabase.from('aql_kits').select('kit_id');

  if (zoneId) query = query.eq('zone_id', zoneId);
  if (params.clientId) query = query.eq('client_id', params.clientId);
  if (params.projectId) query = query.eq('project_id', params.projectId);

  const result = await query;
  const rows = handleQueryError<Record<string, unknown>[]>({ data: result.data, error: result.error }, 'Failed to load scoped kits') ?? [];
  return uniqueStrings(rows.map((row) => row.kit_id));
};

const fetchZoneSensorDeviceIds = async (zoneId: string): Promise<string[]> => {
  const safeZoneId = asUuid(zoneId);
  if (!safeZoneId) return [];
  const result = await supabase
    .from('aql_sensors')
    .select('device_id')
    .eq('zone_id', safeZoneId)
    .not('device_id', 'is', null);

  const rows = handleQueryError<Record<string, unknown>[]>({ data: result.data, error: result.error }, 'Failed to load zone sensor devices') ?? [];
  return uniqueStrings(rows.map((row) => row.device_id));
};

const fetchScopedCameras = async (params: {
  zoneId?: string;
  clientId?: string;
  projectId?: string;
}): Promise<Record<string, unknown>[]> => {
  const zoneId = asUuid(params.zoneId);
  if (zoneId && (params.clientId || params.projectId)) {
    let zoneQuery = supabase
      .from('aql_zones')
      .select('zone_id')
      .eq('zone_id', zoneId)
      .limit(1);
    if (params.clientId) zoneQuery = zoneQuery.eq('client_id', params.clientId);
    if (params.projectId) zoneQuery = zoneQuery.eq('project_id', params.projectId);
    const zoneResult = await zoneQuery;
    const zoneRows = handleQueryError<Record<string, unknown>[]>({ data: zoneResult.data, error: zoneResult.error }, 'Failed to validate camera zone') ?? [];
    if (!zoneRows.length) return [];
  }

  let query = supabase
    .from('aql_cameras')
    .select('*')
    .eq('is_active', true)
    .order('is_primary', { ascending: false })
    .order('created_at', { ascending: true });

  if (zoneId) {
    const zoneKitIds = await fetchZoneKitIds(zoneId);
    query = zoneKitIds.length
      ? query.or(`zone_id.eq.${zoneId},kit_id.in.(${zoneKitIds.join(',')})`)
      : query.eq('zone_id', zoneId);
  } else if (params.clientId || params.projectId) {
    const [kitResult, zoneResult] = await Promise.all([
      (() => {
        let kitQuery = supabase.from('aql_kits').select('kit_id');
        if (params.clientId) kitQuery = kitQuery.eq('client_id', params.clientId);
        if (params.projectId) kitQuery = kitQuery.eq('project_id', params.projectId);
        return kitQuery;
      })(),
      (() => {
        let zoneQuery = supabase.from('aql_zones').select('zone_id');
        if (params.clientId) zoneQuery = zoneQuery.eq('client_id', params.clientId);
        if (params.projectId) zoneQuery = zoneQuery.eq('project_id', params.projectId);
        return zoneQuery;
      })()
    ]);
    const kits = handleQueryError<Record<string, unknown>[]>({ data: kitResult.data, error: kitResult.error }, 'Failed to load client kits') ?? [];
    const zones = handleQueryError<Record<string, unknown>[]>({ data: zoneResult.data, error: zoneResult.error }, 'Failed to load client zones') ?? [];
    const kitIds = uniqueStrings(kits.map((row) => row.kit_id));
    const zoneIds = uniqueStrings(zones.map((row) => row.zone_id));
    if (!kitIds.length && !zoneIds.length) return [];
    if (kitIds.length && zoneIds.length) {
      query = query.or(`kit_id.in.(${kitIds.join(',')}),zone_id.in.(${zoneIds.join(',')})`);
    } else if (kitIds.length) {
      query = query.in('kit_id', kitIds);
    } else {
      query = query.in('zone_id', zoneIds);
    }
  } else {
    return [];
  }

  const result = await query;
  return handleQueryError<Record<string, unknown>[]>({ data: result.data, error: result.error }, 'Failed to load live cameras') ?? [];
};

const feedFromCamera = (camera: Record<string, unknown> | null): LiveCameraFeed | null => {
  if (!camera) return null;

  const cameraId = firstString(camera, ['camera_id']);
  const cameraCode = firstString(camera, ['camera_code']);
  const livePath = normalizeCapturePath(firstString(camera, ['live_feed_path']));
  const sourcePath = normalizeCapturePath(firstString(camera, ['source_uri']));
  const conventionPath = cameraCode ? `live/${cameraCode}/feed.jpg` : cameraId ? `live/${cameraId}/feed.jpg` : null;
  const path = livePath ?? sourcePath ?? conventionPath;
  if (!path) return null;

  const isExternalUrl = /^https?:\/\//i.test(path);
  const modelUrl = firstString(camera, ['browser_model_url']);
  const hasProjectModel = Boolean(firstString(camera, ['vision_lab_project_id']));
  const labels = Array.isArray(camera.browser_model_classes) ? camera.browser_model_classes.map(String) : [];
  return {
    url: isExternalUrl ? path : publicCaptureUrl(path),
    path,
    cameraId,
    kitId: firstString(camera, ['kit_id']),
    cameraCode,
    cameraName: firstString(camera, ['name']),
    lastFrameAt: firstString(camera, ['last_frame_at']),
    lastFrameStatus: firstString(camera, ['last_frame_status']),
    isPrimary: camera.is_primary === true,
    source: livePath ? 'live_feed_path' : sourcePath ? 'camera_source_uri' : 'camera_convention',
    inference: {
      mode: modelUrl ? 'browser-onnx' : hasProjectModel ? 'edge-pytorch' : 'video-only',
      modelUrl,
      modelName: modelUrl ? decodeURIComponent(modelUrl.split('/').pop()?.split(/[?#]/, 1)[0] ?? modelUrl) : null,
      labels,
      inputSize: Math.max(32, Math.min(4096, Math.round(firstNumber(camera, ['browser_model_imgsz']) ?? 640))),
      confidenceThreshold: 0.5,
      source: hasProjectModel ? 'kit' : 'none',
      version: firstNumber(camera, ['browser_model_version'])
    },
    training: trainingStateFromCamera(camera, cameraCode, cameraId)
  };
};

const fetchScopedDeviceIds = async (params: {
  zoneId?: string;
  clientId?: string;
  projectId?: string;
  preferredTypes?: string[];
}): Promise<string[]> => {
  const scopedKitIds = await fetchScopedKitIds({
    zoneId: params.zoneId,
    clientId: params.clientId,
    projectId: params.projectId
  });
  const zoneLinkedKitIds = params.zoneId ? await fetchZoneKitIds(params.zoneId) : [];
  const kitIds = uniqueStrings([...scopedKitIds, ...zoneLinkedKitIds]);
  const sensorDeviceIds = params.zoneId ? await fetchZoneSensorDeviceIds(params.zoneId) : [];

  let query = supabase
    .from('aql_devices')
    .select('device_id, device_type');

  if (kitIds.length > 0) {
    query = query.in('kit_id', kitIds);
  } else if (sensorDeviceIds.length > 0) {
    query = query.in('device_id', sensorDeviceIds);
  } else if (params.clientId && !params.projectId) {
    query = query.eq('client_id', params.clientId);
  } else {
    return [];
  }

  if (params.clientId) {
    query = query.eq('client_id', params.clientId);
  }

  const result = await query;
  const rows = handleQueryError<Record<string, unknown>[]>({ data: result.data, error: result.error }, 'Failed to load devices') ?? [];
  const preferred = params.preferredTypes?.length
    ? rows.filter((row) => params.preferredTypes?.includes(String(row.device_type ?? '').toLowerCase()))
    : [];

  return uniqueStrings((preferred.length ? preferred : rows).map((row) => row.device_id));
};

const isOperationalDeviceRow = (row: Record<string, unknown>) => {
  const status = String(row.status ?? '').trim().toLowerCase();
  return row.is_active !== false && !['inactive', 'disabled', 'offline', 'removed'].includes(status);
};

const fetchScopedUltrasoundDevices = async (params: {
  zoneId?: string;
  clientId?: string;
  projectId?: string;
}): Promise<Record<string, unknown>[]> => {
  const scopedKitIds = await fetchScopedKitIds({
    zoneId: params.zoneId,
    clientId: params.clientId,
    projectId: params.projectId
  });
  const zoneLinkedKitIds = params.zoneId ? await fetchZoneKitIds(params.zoneId) : [];
  const kitIds = uniqueStrings([...scopedKitIds, ...zoneLinkedKitIds]);

  let query = supabase
    .from('aql_devices')
    .select('device_id, client_id, kit_id, name, device_type, status, is_active')
    .eq('device_type', 'ultrasound')
    .eq('is_active', true);

  if (kitIds.length > 0) {
    query = query.in('kit_id', kitIds);
  } else if (params.clientId && !params.projectId && !params.zoneId) {
    query = query.eq('client_id', params.clientId);
  } else {
    return [];
  }

  if (params.clientId) {
    query = query.eq('client_id', params.clientId);
  }

  const result = await query;
  const rows = handleQueryError<Record<string, unknown>[]>({ data: result.data, error: result.error }, 'Failed to load ultrasound devices') ?? [];
  return rows.filter(isOperationalDeviceRow);
};

const sensorMetricFor = (type: unknown): keyof Pick<LiveSensorReading, 'temperature' | 'ph' | 'oxygen' | 'salinity'> | null => {
  const normalized = String(type ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '_');
  if (/(^|_)temp(erature)?(_|$)|temperatura|water_temperature/.test(normalized)) return 'temperature';
  if (normalized === 'ph' || normalized === 'p_h') return 'ph';
  if (/oxygen|oxigenio|oxigeno|dissolved_oxygen|(^|_)o2(_|$)|(^|_)do(_|$)/.test(normalized)) return 'oxygen';
  if (/salinity|salinidade|salinidad|psu/.test(normalized)) return 'salinity';
  return null;
};

export async function resolveActiveZone(params: {
  clientId?: string;
  projectId?: string;
  zoneId?: string;
  locationId?: string;
}): Promise<LiveZoneContext | null> {
  const zoneId = asUuid(params.zoneId);
  if (zoneId) {
    let query = supabase
      .from('aql_zones')
      .select('*')
      .eq('zone_id', zoneId)
      .limit(1);
    if (params.clientId) query = query.eq('client_id', params.clientId);
    if (params.projectId) query = query.eq('project_id', params.projectId);
    const result = await query;
    const rows = handleQueryError<Record<string, unknown>[]>({ data: result.data, error: result.error }, 'Failed to load zone') ?? [];
    const row = rows[0];
    if (!row) return null;
    return {
      zoneId: String(row.zone_id ?? row.id ?? zoneId),
      zoneName: firstString(row, ['name']),
      zoneCode: firstString(row, ['code', 'short_code']),
      locationId: firstString(row, ['location_id'])
    };
  }

  if (!params.clientId) return null;

  let query = supabase
    .from('aql_zones')
    .select('*')
    .eq('client_id', params.clientId)
    .order('created_at', { ascending: true })
    .limit(1);

  if (params.projectId) {
    query = query.eq('project_id', params.projectId);
  }

  if (params.locationId) {
    query = query.eq('location_id', params.locationId);
  }

  const result = await query;
  const rows = handleQueryError<Record<string, unknown>[]>({ data: result.data, error: result.error }, 'Failed to load zones') ?? [];
  const row = rows[0];
  if (!row) return null;
  return {
    zoneId: String(row.zone_id ?? row.id ?? ''),
    zoneName: firstString(row, ['name']),
    zoneCode: firstString(row, ['code', 'short_code']),
    locationId: firstString(row, ['location_id'])
  };
}

export async function resolveLiveCameraFeed(params: {
  zoneId?: string;
  clientId?: string;
  projectId?: string;
}): Promise<LiveCameraFeed | null> {
  const feeds = await fetchLiveCameraFeeds(params);
  return feeds.find((feed) => feed.isPrimary) ?? feeds[0] ?? null;
}

export async function fetchLiveCameraFeeds(params: {
  zoneId?: string;
  clientId?: string;
  projectId?: string;
}): Promise<LiveCameraFeed[]> {
  const cameras = await fetchScopedCameras(params);
  const kitIds = uniqueStrings(cameras.map((camera) => camera.kit_id));
  const kitById = new Map<string, Record<string, unknown>>();
  if (kitIds.length) {
    const result = await supabase
      .from('aql_kits')
      .select('kit_id, vision_lab_project_id, vision_model_policy, vision_model_version')
      .in('kit_id', kitIds);
    if (!result.error) {
      (result.data ?? []).forEach((kit) => kitById.set(String(kit.kit_id), kit));
    }
  }
  const browserModels = new Map<string, NonNullable<Awaited<ReturnType<typeof loadKitBrowserModel>>['browser_model']>>();
  await Promise.all(kitIds.map(async (kitId) => {
    const payload = await loadKitBrowserModel(kitId).catch(() => null);
    if (payload?.browser_model?.url) browserModels.set(kitId, payload.browser_model);
  }));
  const feeds = cameras
    .map((camera) => {
      const kitId = String(camera.kit_id ?? '');
      const browserModel = browserModels.get(kitId);
      return feedFromCamera({
        ...kitById.get(kitId),
        ...(browserModel ? {
          browser_model_url: browserModel.url,
          browser_model_classes: browserModel.classes,
          browser_model_imgsz: browserModel.imgsz,
          browser_model_version: browserModel.version
        } : {}),
        ...camera
      });
    })
    .filter(Boolean) as LiveCameraFeed[];
  return feeds;
}

export async function hasActiveUltrasoundEquipment(params: {
  zoneId?: string;
  clientId?: string;
  projectId?: string;
}): Promise<boolean> {
  if (!params.zoneId && !params.clientId) return false;
  const devices = await fetchScopedUltrasoundDevices(params).catch(() => []);
  return devices.length > 0;
}

const cameraIdentifierFilter = (query: any, params: { cameraId?: string | null; cameraCode?: string | null }) => {
  if (params.cameraId) return query.eq('camera_id', params.cameraId);
  if (params.cameraCode) return query.eq('camera_code', normalizeCameraCode(params.cameraCode));
  return query;
};

export async function fetchCameraTrainingBurstState(params: {
  cameraId?: string | null;
  cameraCode?: string | null;
}): Promise<LiveTrainingBurstState | null> {
  if (!params.cameraId && !params.cameraCode) return null;
  const remote = await invokeTrainingControl('status', params).catch(() => null);
  if (remote) return remote;

  const query = cameraIdentifierFilter(
    supabase.from('aql_cameras').select('*').limit(1),
    params
  );
  const result = await query.maybeSingle();
  const camera = handleQueryError<Record<string, unknown> | null>({ data: result.data, error: result.error }, 'Failed to load camera training burst') ?? null;
  if (!camera) return null;
  const cameraId = firstString(camera, ['camera_id']);
  const cameraCode = firstString(camera, ['camera_code']);
  return trainingStateFromCamera(camera, cameraCode, cameraId);
}

export async function startCameraTrainingBurst(params: {
  cameraId?: string | null;
  cameraCode?: string | null;
  target?: number | null;
}): Promise<LiveTrainingBurstState | null> {
  return await invokeTrainingControl('start', params);
}

export async function stopCameraTrainingBurst(params: {
  cameraId?: string | null;
  cameraCode?: string | null;
}): Promise<LiveTrainingBurstState | null> {
  return await invokeTrainingControl('stop', params);
}

export async function copyCurrentCameraTrainingFrame(params: {
  cameraId?: string | null;
  cameraCode?: string | null;
}): Promise<LiveTrainingBurstState | null> {
  return await invokeTrainingControl('copy-current', params);
}

export async function fetchCameraTrainingDataset(params: {
  cameraId?: string | null;
  cameraCode?: string | null;
}): Promise<LiveTrainingDatasetExport> {
  const body = {
    camera_id: params.cameraId ?? null,
    camera_code: params.cameraCode ?? null
  };
  const { data, error } = await supabase.functions.invoke('vision-captures/training-export', { body });
  if (error) {
    let detail = error.message;
    const context = (error as { context?: unknown }).context;
    if (context) {
      const parsed = await readFunctionErrorContext(context);
      detail = parsed.message ? `${parsed.status ?? ''}: ${parsed.message}`.trim() : parsed.status ? `${parsed.status}: ${detail}` : detail;
    }
    throw new Error(detail);
  }

  const payload = asRecord(data);
  if (payload.error) throw new Error(String(payload.error));

  const images = Array.isArray(payload.images)
    ? payload.images
      .map((item) => {
        const row = asRecord(item);
        const name = firstString(row, ['name']) ?? '';
        const path = firstString(row, ['path']) ?? '';
        const url = firstString(row, ['url', 'publicUrl', 'public_url']) ?? (path ? publicCaptureUrl(path) : '');
        if (!name || !path || !url) return null;
        return {
          name,
          path,
          url,
          size: firstNumber(row, ['size']),
          contentType: firstString(row, ['contentType', 'content_type', 'mimetype']),
          createdAt: firstString(row, ['createdAt', 'created_at']),
          updatedAt: firstString(row, ['updatedAt', 'updated_at'])
        } satisfies LiveTrainingDatasetImage;
      })
      .filter(Boolean) as LiveTrainingDatasetImage[]
    : [];

  return {
    bucket: firstString(payload, ['bucket']) ?? 'captures',
    folderPath: firstString(payload, ['folderPath', 'folder_path']) ?? null,
    imageCount: firstNumber(payload, ['imageCount', 'image_count']) ?? images.length,
    totalFolderImageCount: firstNumber(payload, ['totalFolderImageCount', 'total_folder_image_count']) ?? images.length,
    training: trainingStateFromPayload(payload.training),
    images
  };
}

export async function fetchLatestCapture(params: {
  zoneId?: string;
  clientId?: string;
  projectId?: string;
}): Promise<LiveCapture | null> {
  if (!params.zoneId && !params.clientId) return null;

  const deviceIds = await fetchScopedDeviceIds({
    zoneId: params.zoneId,
    clientId: params.clientId,
    projectId: params.projectId,
    preferredTypes: ['camera']
  });
  if (deviceIds.length === 0) return null;

  let query = supabase
    .from('aql_vision_captures')
    .select('*')
    .in('device_id', deviceIds)
    .order('timestamp', { ascending: false })
    .limit(1);

  const result = await query;
  const rows = handleQueryError<Record<string, unknown>[]>({ data: result.data, error: result.error }, 'Failed to load latest capture') ?? [];
  const row = rows[0];
  if (!row) return null;

  return {
    id: String(row.capture_id ?? row.id ?? ''),
    zone_id: params.zoneId ?? firstString(row, ['zone_id', 'tank_id']),
    image_url: firstString(row, ['image_url', 'imageUrl', 'image_path', 'storage_path', 'url']),
    captured_at: String(row.timestamp ?? row.created_at ?? new Date().toISOString()),
    species: firstString(row, ['species']),
    phase: firstString(row, ['biological_phase', 'phase'])
  };
}

export async function fetchLatestSensorReading(params: {
  zoneId?: string;
  clientId?: string;
  projectId?: string;
}): Promise<LiveSensorReading | null> {
  if (!params.zoneId && !params.clientId) return null;

  const deviceIds = await fetchScopedDeviceIds({
    zoneId: params.zoneId,
    clientId: params.clientId,
    projectId: params.projectId,
    preferredTypes: ['sensor']
  });

  let scopedQuery = supabase
    .from('aql_sensor_readings')
    .select('*')
    .order('timestamp', { ascending: false })
    .limit(80);

  if (params.clientId) scopedQuery = scopedQuery.eq('client_id', params.clientId);

  const scopedResult = await scopedQuery;
  let rows = handleQueryError<Record<string, unknown>[]>({ data: scopedResult.data, error: scopedResult.error }, 'Failed to load sensor readings') ?? [];
  if (rows.length === 0 && deviceIds.length > 0) {
    const deviceResult = await supabase
      .from('aql_sensor_readings')
      .select('*')
      .in('device_id', deviceIds)
      .order('timestamp', { ascending: false })
      .limit(80);
    rows = handleQueryError<Record<string, unknown>[]>({ data: deviceResult.data, error: deviceResult.error }, 'Failed to load sensor readings by device') ?? [];
  }
  if (rows.length === 0) return null;

  const latest: LiveSensorReading = {
    zone_id: params.zoneId ?? null,
    temperature: null,
    ph: null,
    oxygen: null,
    salinity: null,
    recorded_at: String(rows[0].timestamp ?? rows[0].recorded_at ?? rows[0].created_at ?? new Date().toISOString())
  };

  rows.forEach((row) => {
    const metadata = asRecord(row.metadata);
    const source = { ...metadata, ...row };
    const combinedValues: Array<[keyof Pick<LiveSensorReading, 'temperature' | 'ph' | 'oxygen' | 'salinity'>, number | null]> = [
      ['temperature', firstNumber(source, ['temperature', 'temp', 'water_temperature', 'water_temperature_c', 'temperature_c', 'temp_c'])],
      ['ph', firstNumber(source, ['ph', 'pH'])],
      ['oxygen', firstNumber(source, ['oxygen', 'dissolved_oxygen', 'do_mg_l', 'oxygen_mg_l', 'o2'])],
      ['salinity', firstNumber(source, ['salinity', 'salinity_psu', 'psu'])]
    ];

    combinedValues.forEach(([metric, value]) => {
      if (latest[metric] === null && value !== null) latest[metric] = value;
    });

    const typedMetric = sensorMetricFor(row.sensor_type ?? metadata.sensor_type ?? metadata.type);
    const typedValue = firstNumber(source, ['value', 'last_value', 'reading']);
    if (typedMetric && latest[typedMetric] === null && typedValue !== null) {
      latest[typedMetric] = typedValue;
    }
  });

  return latest;
}

export async function fetchDetectionCount(captureId: string): Promise<number | null> {
  if (!captureId) return null;
  const result = await supabase
    .from('aql_detections')
    .select('detection_id', { count: 'exact', head: true })
    .eq('capture_id', captureId);

  if (result.error) {
    throw new Error(`Failed to load detections: ${result.error.message ?? result.error}`);
  }
  return result.count ?? null;
}
